import React from 'react';
import { Section, FadeIn, StaggerContainer, StaggerItem } from './ui';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
export const ProblemSection = () => {
  const familiarPoints = ['Te despiertas cansada aunque hayas dormido 7 u 8 horas', 'Llegas al mediodía y ya no tienes energía para nada', 'La niebla mental aparece justo cuando más necesitas claridad', 'Los antojos llegan sin aviso y arruinan todo lo que intentas hacer bien', 'Sientes que tu cuerpo ya no responde como antes', 'Has probado de todo — y nada funciona por más de unos días'];
  const traditionalFails = [{
    attempt: 'Dormir más horas',
    why: 'No resuelve la calidad del sueño hormonal'
  }, {
    attempt: 'Tomar más café',
    why: 'Agota más las glándulas suprarrenales'
  }, {
    attempt: 'Dietas restrictivas',
    why: 'Generan más estrés hormonal'
  }, {
    attempt: 'Suplementos genéricos',
    why: 'No están sincronizados con tu ciclo biológico actual'
  }, {
    attempt: 'Ejercicio intenso',
    why: 'Puede aumentar el cortisol y empeorar el agotamiento'
  }];
  return <>
      {/* Emotional Lead */}
      <Section bg="bg-cream" data-id="element-230">
        <FadeIn data-id="element-231">
          <div className="max-w-3xl mx-auto text-center" data-id="element-232">
            <h2 className="text-3xl md:text-5xl font-bold mb-10 leading-tight" data-id="element-233">
              Hay un tipo de cansancio que no se quita durmiendo.
            </h2>
            <div className="space-y-5 text-lg text-brown-light leading-relaxed" data-id="element-234">
              <p data-id="element-235">
                No importa cuántas horas pases en cama. No importa si te
                acuestas temprano, si apagas el teléfono, si tomas melatonina o
                si sigues todos los consejos que has leído en internet.
              </p>
              <p data-id="element-236">
                Te despiertas y el cuerpo ya está pesado. La mente tarda en
                arrancar.
              </p>
              <p className="text-brown font-medium italic" data-id="element-237">
                Y en algún lugar entre el café y el espejo, te preguntas en
                silencio: "¿Por qué me siento así si supuestamente dormí?"
              </p>
            </div>

            <div className="mt-12 bg-white border-l-4 border-sage p-8 rounded-2xl text-left" data-id="element-238">
              <p className="text-xl text-brown font-medium leading-relaxed" data-id="element-239">
                No estás exagerando.
                <br data-id="element-240" />
                No estás siendo débil.
                <br data-id="element-241" />Y definitivamente no estás sola.
              </p>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* "Te suena familiar?" */}
      <Section bg="bg-cream-light" data-id="element-242">
        <FadeIn data-id="element-243">
          <div className="max-w-4xl mx-auto" data-id="element-244">
            <div className="text-center mb-12" data-id="element-245">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-246">
                ¿Te suena familiar alguna de estas situaciones?
              </h2>
              <p className="text-brown-light" data-id="element-247">
                Si respondiste que sí a cualquiera de estas, esto fue escrito
                para ti.
              </p>
            </div>

            <StaggerContainer className="grid md:grid-cols-2 gap-4" data-id="element-248">
              {familiarPoints.map((point, i) => <StaggerItem key={i} data-id="element-249">
                  <div className="flex gap-4 items-start bg-white p-6 rounded-2xl shadow-sm border border-cream-dark" data-id="element-250">
                    <div className="mt-0.5 shrink-0 w-8 h-8 bg-sage-light rounded-full flex items-center justify-center" data-id="element-251">
                      <CheckCircle className="w-5 h-5 text-sage" data-id="element-252" />
                    </div>
                    <p className="text-brown-light leading-relaxed" data-id="element-253">{point}</p>
                  </div>
                </StaggerItem>)}
            </StaggerContainer>
          </div>
        </FadeIn>
      </Section>

      {/* Why Traditional Solutions Fail */}
      <Section bg="bg-white" data-id="element-254">
        <FadeIn data-id="element-255">
          <div className="max-w-4xl mx-auto" data-id="element-256">
            <div className="text-center mb-12" data-id="element-257">
              <span className="inline-flex items-center gap-2 text-coral font-bold tracking-wider uppercase text-sm mb-4" data-id="element-258">
                <AlertCircle className="w-4 h-4" data-id="element-259" />
                El problema no eres tú
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-260">
                Por qué las soluciones tradicionales fallan
              </h2>
              <p className="text-brown-light max-w-2xl mx-auto" data-id="element-261">
                La mayoría de los consejos para la fatiga menopáusica están
                diseñados para cuerpos más jóvenes — o para síntomas aislados
                que no consideran el cuadro completo.
              </p>
            </div>

            <div className="bg-cream-light rounded-3xl overflow-hidden border border-cream-dark" data-id="element-262">
              <div className="grid grid-cols-12 bg-brown text-cream-light px-6 py-4 text-sm font-bold uppercase tracking-wider" data-id="element-263">
                <div className="col-span-5" data-id="element-264">Solución tradicional</div>
                <div className="col-span-7" data-id="element-265">Por qué falla</div>
              </div>
              {traditionalFails.map((row, i) => <div key={i} className={`grid grid-cols-12 px-6 py-5 items-start gap-4 ${i !== traditionalFails.length - 1 ? 'border-b border-cream-dark' : ''}`} data-id="element-266">
                  <div className="col-span-5 flex items-start gap-3 font-bold text-brown" data-id="element-267">
                    <XCircle className="w-5 h-5 text-coral mt-0.5 shrink-0" data-id="element-268" />
                    <span data-id="element-269">{row.attempt}</span>
                  </div>
                  <div className="col-span-7 text-brown-light" data-id="element-270">{row.why}</div>
                </div>)}
            </div>

            <div className="mt-12 text-center max-w-2xl mx-auto" data-id="element-271">
              <p className="text-xl md:text-2xl text-brown font-medium leading-relaxed" data-id="element-272">
                El cuerpo de una mujer en menopausia necesita un enfoque
                diferente.
              </p>
              <p className="text-lg text-gold font-bold italic mt-4" data-id="element-273">
                No más esfuerzo. Esfuerzo sincronizado.
              </p>
            </div>
          </div>
        </FadeIn>
      </Section>
    </>;
};